import React from "react";
import { motion } from "framer-motion";
import { fadeIn } from "../animations";
import { useTranslation } from "react-i18next";

interface ErrorProps {
  error: string;
  onRetry: () => void;
  showRetryButton?: boolean;
}

export const ErrorMessage: React.FC<ErrorProps> = ({
  error,
  onRetry,
  showRetryButton = true,
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="min-h-[400px] flex items-center justify-center"
      variants={fadeIn}
      initial="initial"
      animate="animate"
    >
      <div className="text-center max-w-md">
        <div className="mx-auto w-16 h-16 mb-6 text-red-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-2xl mb-3">{t("lands.error.title")}</h2>
        <p className="text-gray-600 mb-8">{error}</p>
        {showRetryButton && (
          <button
            className="px-6 py-3 bg-black text-white rounded-none hover:bg-gray-800 transition duration-300"
            onClick={onRetry}
          >
            {t("lands.error.tryAgain")}
          </button>
        )}
      </div>
    </motion.div>
  );
};

interface NoResultsProps {
  onResetFilters: () => void;
}

export const NoResults: React.FC<NoResultsProps> = ({ onResetFilters }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="min-h-[400px] flex items-center justify-center"
      variants={fadeIn}
      initial="initial"
      animate="animate"
    >
      <div className="text-center max-w-md">
        <svg
          className="w-16 h-16 mx-auto mb-6 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h2 className="text-2xl mb-3">{t("lands.noResults.title")}</h2>
        <p className="text-gray-600 mb-8">{t("lands.noResults.message")}</p>
        <button
          className="px-6 py-3 bg-black text-white rounded-none hover:bg-gray-800 transition duration-300"
          onClick={onResetFilters}
        >
          {t("lands.noResults.resetFilters")}
        </button>
      </div>
    </motion.div>
  );
};
